import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader, Card, InfoTip } from "../components/dc";

const PINK = "#ec4899", CYAN = "#67e0f0", GREEN = "#34d399", AMBER = "#fbbf24", RED = "#fb7185", MUTE = "#9d8fc0";
const gbp0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
const pct1 = (n) => `${Number(n || 0).toFixed(1)}%`;
const signPct = (n) => (n == null ? "—" : `${n >= 0 ? "+" : ""}${Number(n).toFixed(1)}%`);

// PriceGuard supplier name → Supply Scanner supplier name (to attach OSS orders).
const SS_ALIAS = {
  CEF: "City Electric Supply", CPS: "City Plumbing Supplies", WOLSELEY: "Wolseley",
  YESSS: "YESSS Electrical", "TRAVIS PERKINS": "Travis Perkins", UKPS: "UK Plumbing Supplies",
};

// Score reflects PRICING + data quality only. Supply Scanner (OSS) is shown as a
// supplementary data point — orders also flow through the warehouses, so it isn't
// a fair measure of a supplier's total service and doesn't drive the score.
function rag(coverage) {
  if (coverage < 75) return { label: "Attention", color: RED };
  if (coverage >= 90) return { label: "Healthy", color: GREEN };
  return { label: "Watch", color: AMBER };
}

function talkingPoint(p) {
  const bits = [];
  if (p.coverage < 75) bits.push(`price-file coverage only ${Math.round(p.coverage)}%`);
  if (p.net > 10000) bits.push(`${gbp0(p.net)} net overcharge outstanding`);
  if (p.change != null && Math.abs(p.change) >= 8) bits.push(`prices ${p.change >= 0 ? "up" : "down"} ${signPct(p.change)} recently`);
  if (!bits.length) return "Pricing and coverage look healthy.";
  const s = bits.slice(0, 2).join(", and ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
}

const Stat = ({ label, value, color }) => (
  <div style={{ minWidth: 78 }}>
    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: MUTE, marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || "var(--color-text-1)", fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>
);

function ScoreCard({ p }) {
  const r = rag(p.coverage);
  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0, overflowWrap: "anywhere", fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, color: "var(--color-text-1)" }}>{p.supplier}</div>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#0c0818", background: r.color, borderRadius: 999, padding: "3px 10px" }}>{r.label}</span>
      </div>

      <div>
        <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".06em", color: PINK, fontWeight: 700, marginBottom: 6 }}>Pricing</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
          <Stat label="Overcharge" value={gbp0(p.overcharge)} color={p.overcharge > 0 ? RED : GREEN} />
          <Stat label="Net after credits" value={gbp0(p.net)} />
          <Stat label="Coverage" value={pct1(p.coverage)} color={p.coverage >= 90 ? GREEN : p.coverage >= 75 ? AMBER : RED} />
          <Stat label="Priced" value={pct1(p.matched)} />
          <Stat label="Price move 3m" value={signPct(p.change)} color={p.change > 0 ? RED : p.change < 0 ? GREEN : MUTE} />
          <Stat label="SKUs" value={(p.skus || 0).toLocaleString()} />
        </div>
      </div>

      <div style={{ fontSize: 12.5, color: "var(--color-text-2)", borderTop: "1px solid var(--hairline)", paddingTop: 10 }}>{talkingPoint(p)}</div>

      {p.hasService && (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, fontSize: 11.5, color: MUTE, borderTop: "1px solid var(--hairline)", paddingTop: 8 }}>
          <span style={{ color: CYAN, fontWeight: 600 }}>OSS orders</span>
          <span>{(p.orders || 0).toLocaleString()} via Supply Scanner</span>
          <span>{Math.round(p.success)}% completed</span>
          <span>{gbp0(p.ssSpend)} spend</span>
          <InfoTip text="Supply Scanner (OSS) orders only — a supplementary data point, not the supplier's total service. Orders also flow through the warehouses, so this doesn't drive the score." />
        </div>
      )}
    </Card>
  );
}

export default function QbrScorecards() {
  usePageTitle("QBR — Scorecards");
  const navigate = useNavigate();
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pm, inf, ss] = await Promise.all([
          apiGet("/dashboard/supplier-metrics"),
          apiGet("/dashboard/inflation-index"),
          apiGet("/supply-scanner/stats"),
        ]);
        const infMap = {};
        (inf?.summary || []).forEach((s) => { infMap[(s.supplier || "").toUpperCase()] = s; });
        const covMap = {};
        (ss?.coverage || []).forEach((c) => { covMap[(c.supplier || "").toUpperCase()] = c; });
        const spendMap = {};
        (ss?.supplierChart || []).forEach((c) => { spendMap[(c.label || "").toUpperCase()] = c.spend; });

        const rows = (pm?.metrics || []).map((m) => {
          const up = (m.supplier || "").toUpperCase();
          const info = infMap[up] || {};
          const ssName = (SS_ALIAS[up] || m.supplier || "").toUpperCase();
          const cov = covMap[ssName];
          return {
            supplier: m.supplier,
            overcharge: m.overcharge_value || 0,
            net: m.net_overcharge != null ? m.net_overcharge : (m.overcharge_value || 0),
            coverage: m.price_file_coverage_pct || 0,
            matched: m.matched_pct || 0,
            change: info.three_m_pct != null ? info.three_m_pct : null,
            skus: m.total_products || 0,
            hasService: !!cov,
            orders: cov?.total || 0,
            success: cov?.completedPct || 0,
            ssSpend: spendMap[ssName] || 0,
          };
        });
        rows.sort((a, b) => (b.overcharge || 0) - (a.overcharge || 0));
        setCards(rows);
      } catch { setCards([]); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="qbr-scorecards">
      <PageHeader title="Supplier Scorecards" subtitle="Pricing performance per supplier, with OSS order activity as a side data point" />
      {loading && <div style={{ color: MUTE, padding: 40 }}>Loading…</div>}
      {!loading && cards && cards.length === 0 && (
        <Card><div style={{ color: MUTE, fontSize: 14 }}>No supplier data yet — once PriceGuard analytics have run, scorecards populate here.</div></Card>
      )}
      {!loading && cards && cards.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 360px), 1fr))", gap: 16 }}>
          {cards.map((p) => (
            <div key={p.supplier} role="link" tabIndex={0} onKeyDown={e => { if (e.key === "Enter") navigate(`/qbr/supplier/${encodeURIComponent(p.supplier)}`); }} onClick={() => navigate(`/qbr/supplier/${encodeURIComponent(p.supplier)}`)}
              style={{ cursor: "pointer" }} data-testid={`scorecard-open-${p.supplier}`}>
              <ScoreCard p={p} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
